import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import config from "../../../../configuration/config";

/* Static (locale-agnostic) day map. Keep at module-scope so it's not
   re-allocated on every render. */
const DAY_DEFS = [
    { idx: 6, key: "Saturday",  i18n: "sat"  },
    { idx: 0, key: "Sunday",    i18n: "sun"  },
    { idx: 1, key: "Monday",    i18n: "mon"  },
    { idx: 2, key: "Tuesday",   i18n: "tues" },
    { idx: 3, key: "Wednesday", i18n: "wed"  },
    { idx: 4, key: "Thursday",  i18n: "thur" },
    { idx: 5, key: "Friday",    i18n: "fri"  },
];

/** Build the 7 dates of the current Saturday-anchored week. */
function buildWeekDates() {
    const now = new Date();
    const daysSinceSaturday = (now.getDay() + 1) % 7;
    const start = new Date(now);
    start.setDate(now.getDate() - daysSinceSaturday);
    return Array.from({ length: 7 }, (_, i) => {
        const d = new Date(start);
        d.setDate(start.getDate() + i);
        return {
            dayNameEn: d.toLocaleDateString("en-US", { weekday: "long" }),
            day: d.getDate(),
            month: d.getMonth() + 1,
        };
    });
}

/** Convert "HH:MM:SS.fff" → "8 ص" / "8 AM". Returns null on bad/zero values. */
function formatHour(timeStr, isAr) {
    if (!timeStr || timeStr === "00:00:00.0000000") return null;
    const [h, m] = timeStr.split(":");
    let hour = parseInt(h, 10);
    const min = parseInt(m, 10);
    if (Number.isNaN(hour)) return null;
    const isPM = hour >= 12;
    hour = hour % 12 || 12;
    const minPart = min ? `:${String(min).padStart(2, "0")}` : "";
    const suffix = isAr ? (isPM ? "م" : "ص") : (isPM ? "PM" : "AM");
    return `${hour}${minPart} ${suffix}`;
}

export function NavWorkHours({ department = 12 }) {
    const { t: trans, i18n } = useTranslation(["navbar", "model"]);
    const lang = i18n.language || "ar";
    const isAr = lang === "ar";
    const dirClass = `language-${lang}`;

    const [workingHours, setWorkingHours] = useState(null);
    const [holidaysList, setHolidaysList] = useState(null);

    const weekDatesList = useMemo(buildWeekDates, []);
    const dayLabels = useMemo(
        () => DAY_DEFS.map((d) => ({ ...d, label: trans(`navbar:${d.i18n}`) })),
        [trans]
    );

    /* 1) Fetch holidays (or fail gracefully). */
    useEffect(() => {
        let cancelled = false;
        axios.post(
            `${config.controllURL}${lang}/api/Pages/fun_GetHolidays`,
            { lang }
        )
            .then((res) => { if (!cancelled) setHolidaysList(Array.isArray(res.data) ? res.data : []); })
            .catch(() => { if (!cancelled) setHolidaysList([]); });
        return () => { cancelled = true; };
    }, [lang]);

    /* 2) Fetch the calendar once holidays are known, then build rows. */
    useEffect(() => {
        if (holidaysList === null) return;
        let cancelled = false;
        axios.post(
            `${config.controllURL}${lang}/api/Pages/SiteGetCalenderDetails`,
            { CalenderType: department }
        ).then((res) => {
            if (cancelled) return;
            const data = res.data?.[0];
            if (!data) { setWorkingHours([]); return; }

            const closedLbl = trans("navbar:no_work");
            const fromLbl = isAr ? "من" : "From";
            const toLbl   = isAr ? "حتى" : "to";

            const rows = dayLabels.map(({ idx, key, label }) => {
                const isOpen = data[key] === 1 || data[key] === true;
                if (!isOpen) return { day: idx, name: label, summary: closedLbl, isClosed: true };

                const weekDay = weekDatesList.find(
                    (w) => w.dayNameEn.toLowerCase() === key.toLowerCase()
                );
                const holiday = weekDay && holidaysList.find(
                    (h) => h.Day === weekDay.day && h.Month === weekDay.month
                );
                if (holiday) return {
                    day: idx,
                    name: label,
                    summary: holiday.Description || closedLbl,
                    isClosed: true,
                };

                const s1From = formatHour(data.Shift1_From, isAr);
                const s1To   = formatHour(data.Shift1_To,   isAr);
                const s2To   = data.ShiftCount === 2 ? formatHour(data.Shift2_To, isAr) : null;

                const start = s1From;
                const end = s2To || s1To;
                if (!start || !end) return { day: idx, name: label, summary: closedLbl, isClosed: true };

                return {
                    day: idx,
                    name: label,
                    summary: isAr ? `${fromLbl} ${start} ${toLbl} ${end}` : `${start} – ${end}`,
                    isClosed: false,
                };
            });
            setWorkingHours(rows);
        }).catch(() => { if (!cancelled) setWorkingHours([]); });
        return () => { cancelled = true; };
    }, [department, holidaysList, dayLabels, weekDatesList, lang, isAr, trans]);

    if (workingHours === null) {
        return (
            <Skeleton
                count={1}
                borderRadius="1rem"
                width="100%"
                height="100%"
                containerClassName="nav-maintenance-container"
                inline
            />
        );
    }

    const todayIdx = new Date().getDay();

    return (
        <ul className={`work-hours-flex whx-list ${dirClass}`}>
            {workingHours.map((ele) => {
                const active = ele.day === todayIdx;
                return (
                    <li
                        key={ele.day}
                        className={`whx-row${active ? " active" : ""}${ele.isClosed ? " closed" : ""}`}
                    >
                        <span className="whx-icon" aria-hidden="true">
                            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10" />
                                <polyline points="12 6 12 12 16 14" />
                            </svg>
                        </span>
                        <span className="whx-day">{ele.name}</span>
                        <span className="whx-hours">{ele.summary}</span>
                    </li>
                );
            })}
        </ul>
    );
}
